import React from 'react';
import { ReplacementVehicleRequest } from '../types';
import { Clock, MapPin, User, Phone, Mail, Circle, Navigation } from 'react-feather';
import './RequestsView.css';

interface RequestsViewProps {
  requests: ReplacementVehicleRequest[];
  onViewTracking: (request: ReplacementVehicleRequest) => void;
}

export const RequestsView: React.FC<RequestsViewProps> = ({ requests, onViewTracking }) => {
  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const formatETA = (eta?: Date) => {
    if (!eta) return 'Calculating...';
    
    const now = new Date();
    const diffInMinutes = Math.floor((eta.getTime() - now.getTime()) / (1000 * 60));
    
    if (diffInMinutes <= 0) return 'Arriving now';
    if (diffInMinutes < 60) return `${diffInMinutes} min`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    const remainingMinutes = diffInMinutes % 60;
    
    if (remainingMinutes === 0) return `${diffInHours}h`;
    return `${diffInHours}h ${remainingMinutes}m`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'dispatched': return '#3b82f6';
      case 'en-route': return '#10b981';
      case 'arrived': return '#059669';
      case 'completed': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'dispatched': return 'Dispatched';
      case 'en-route': return 'En Route';
      case 'arrived': return 'Arrived';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  if (requests.length === 0) {
    return (
      <div className="requests-view">
        <div className="requests-header">
          <h2>Replacement Vehicle Requests</h2>
          <p>Your submitted requests will appear here</p>
        </div>
        <div className="empty-state">
                      <Circle size={48} className="empty-icon" />
          <h3>No requests yet</h3>
          <p>When you request a replacement vehicle from an accident, it will appear here for tracking.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="requests-view">
      <div className="requests-header">
        <h2>Replacement Vehicle Requests</h2>
        <p>{requests.length} active request{requests.length !== 1 ? 's' : ''}</p>
      </div>
      
      <div className="requests-list">
        {requests.map((request) => (
          <div key={request.id} className="request-card">
            <div className="request-header">
              <div className="request-status">
                <div 
                  className="status-indicator"
                  style={{ backgroundColor: getStatusColor(request.status) }}
                />
                <span className="status-text">{getStatusText(request.status)}</span>
              </div>
              <div className="request-time">
                {formatTimeAgo(request.requestedAt)}
              </div>
            </div>
            
            <div className="request-content">
              <h3 className="accident-title">{request.accident.title}</h3>
              
              <div className="accident-details">
                <div className="accident-detail">
                  <MapPin size={14} className="detail-icon" />
                  <span>{request.accident.location.address}</span>
                </div>
              </div>
              
              <div className="customer-details">
                <div className="customer-detail">
                  <User size={14} className="detail-icon" />
                  <span>{request.leadInfo.fullName}</span>
                </div>
                <div className="customer-detail">
                  <Phone size={14} className="detail-icon" />
                  <span>{request.leadInfo.phone}</span>
                </div>
                <div className="customer-detail">
                  <Mail size={14} className="detail-icon" />
                  <span>{request.leadInfo.email}</span>
                </div>
              </div>
              
              {request.estimatedArrival && (
                <div className="eta-section">
                  <Clock size={14} className="detail-icon" />
                  <span>ETA: {formatETA(request.estimatedArrival)}</span>
                </div>
              )}
            </div>
            
            {(request.status === 'dispatched' || request.status === 'en-route') && (
              <div className="request-actions">
                <button 
                  className="btn-track-vehicle"
                  onClick={() => onViewTracking(request)}
                >
                  <Navigation size={14} />
                  Track Vehicle
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}; 